import { useField, useFormikContext } from 'formik'
import React from 'react'

import FieldError from '@/components/form/FieldError'
import FieldLabel, { TooltipHint } from '@/components/form/FieldLabel'
import RadioOptions, {
  RadioOptionsProps,
  RadioOptionsType,
} from '@/components/misc/RadioOptions'
import { FieldGroup } from '@/components/ui/field'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

export type RadioOptionsInputProps<TValue> = RadioOptionsProps<TValue> & {
  name: string
  label?: React.ReactNode
  transformErrorMessage?: (originalError: string) => React.ReactElement | string
  tooltipHint?: TooltipHint
  labelClassName?: string
  isProcessing?: boolean
}

function RadioOptionsInput<TValue extends RadioOptionsType>({
  name,
  label,
  options,
  onChange,
  tooltipHint,
  labelClassName,
  transformErrorMessage,
  isProcessing,
  ...props
}: RadioOptionsInputProps<TValue>) {
  const { submitCount, setFieldValue } = useFormikContext()
  const [field, { error }] = useField(name)
  const id = `field_${name}`
  const showError = !!(submitCount > 0 && error)

  function handleChange(value: TValue) {
    setFieldValue(name, value)

    if (onChange) {
      onChange(value)
    }
  }

  return (
    <FieldGroup>
      {label && (
        <FieldLabel
          htmlFor={id}
          tooltipHint={tooltipHint}
          className={cn('mb-1', labelClassName)}
        >
          {label}
        </FieldLabel>
      )}

      <div className="flex items-center gap-4">
        <RadioOptions
          options={options}
          value={field.value}
          onChange={handleChange}
          inputName={name}
          {...props}
        />
        {isProcessing && <Spinner />}
      </div>

      {showError && (
        <FieldError inputId={id}>
          {transformErrorMessage ? transformErrorMessage(error) : error}
        </FieldError>
      )}
    </FieldGroup>
  )
}

export default RadioOptionsInput
